/**
 * Contracts API - REST client for contract management
 * Epic 26: Szerződés kezelés (bérleti, szerviz, értékesítési)
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type ContractStatus = 'draft' | 'active' | 'completed' | 'cancelled';
export type ContractType = 'rental' | 'service' | 'sales';

export interface ContractItem {
  id: string;
  productId?: string;
  description: string;
  quantity: number;
  dailyRate?: number;
  totalAmount: number;
}

export interface Contract {
  id: string;
  tenantId: string;
  contractNumber: string;
  type: ContractType;
  status: ContractStatus;
  rentalId?: string;
  rentalNumber?: string;
  partnerId: string;
  partnerName: string;
  partnerAddress?: string;
  partnerPhone?: string;
  partnerIdNumber?: string;
  createdAt: string;
  updatedAt: string;
  startDate: string;
  expectedEndDate: string;
  actualEndDate?: string;
  signedAt?: string;
  items: ContractItem[];
  totalAmount: number;
  depositAmount: number;
  depositPaid: boolean;
  terms?: string;
  notes?: string;
  createdBy: string;
  signedByPartner?: boolean;
  signedByStaff?: boolean;
}

export interface ContractFilters {
  search?: string;
  status?: ContractStatus;
  type?: ContractType;
  page?: number;
  pageSize?: number;
}

export interface ContractListResponse {
  data: Contract[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface ContractResponse {
  data: Contract;
}

// Backend-managed fields that frontend must never send on create
export type CreateContractData = Omit<
  Contract,
  | 'id'
  | 'tenantId'
  | 'createdAt'
  | 'updatedAt'
  | 'contractNumber'
  | 'createdBy'
  | 'signedAt'
  | 'actualEndDate'
  | 'signedByPartner'
  | 'signedByStaff'
>;
// Immutable/backend-managed fields excluded from patch
export type UpdateContractData = Partial<
  Omit<Contract, 'id' | 'tenantId' | 'createdAt' | 'updatedAt' | 'contractNumber' | 'createdBy'>
>;

// ============================================
// API FUNCTIONS
// ============================================

export async function getContracts(filters: ContractFilters = {}): Promise<ContractListResponse> {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.type) params.append('type', filters.type);
  if (filters.page !== undefined) params.append('page', String(filters.page));
  if (filters.pageSize !== undefined) params.append('pageSize', String(filters.pageSize));
  const query = params.toString();
  return api.get<ContractListResponse>(`/contracts${query ? `?${query}` : ''}`);
}

export async function getContractById(id: string): Promise<ContractResponse> {
  return api.get<ContractResponse>(`/contracts/${id}`);
}

export async function createContract(data: CreateContractData): Promise<ContractResponse> {
  return api.post<ContractResponse>('/contracts', data);
}

export async function updateContract(
  id: string,
  data: UpdateContractData
): Promise<ContractResponse> {
  return api.patch<ContractResponse>(`/contracts/${id}`, data);
}

export async function deleteContract(id: string): Promise<void> {
  return api.delete<void>(`/contracts/${id}`);
}
